import { useEffect, useRef, useState } from "react";
import { FileText, Loader2, Save, Send } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { api, errorMessage } from "../lib/api";

const FIELDS = [
  ["piece", "Valor por peça cortada"],
  ["hinge", "Valor por furação (dobradiça)"],
  ["channel", "Valor por canal de fundo de gaveta"],
  ["rebaixo", "Valor por rebaixo / canal de LED"],
  ["edge_m", "Valor por metro de fitagem (mão de obra)"],
  ["programacao", "Valor Programação"],
];

const PriceRow = ({ label, testId, value, placeholder, onChange }) => (
  <div className="grid grid-cols-[1fr_90px] sm:grid-cols-[1fr_110px] items-center gap-2">
    <Label className="text-xs text-[#9CA3AF] break-words">{label}</Label>
    <Input data-testid={testId} type="number" min={0} step="0.01" value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)}
      className="h-9 bg-[#0F1115] border-[#2D333F] font-mono text-right" />
  </div>
);

const same = (a, b) => a.color === b.color && (a.thickness ?? null) === (b.thickness ?? null);

// Per-color prices: empty input removes the entry so the default value is used
const ColorPrices = ({ budget, prices, setPrices }) => {
  const lines = (budget?.lines || []).filter((l) => l.color !== undefined);
  if (!lines.length) return null;
  const update = (listKey, entry, raw) => setPrices((o) => {
    const rest = (o[listKey] || []).filter((x) => !same(x, entry));
    return { ...o, [listKey]: raw === "" ? rest : [...rest, { ...entry, price: Number(raw) }] };
  });
  return (
    <div className="space-y-3 pt-2 border-t border-[#2D333F]">
      <p className="font-heading font-semibold">Valor de material (R$)</p>
      {lines.map((l) => {
        const isEdge = l.key === "edge_tape_m";
        const i = lines.filter((x) => (x.key === "edge_tape_m") === isEdge).indexOf(l);
        const listKey = isEdge ? "edge_colors" : "sheet_colors";
        const entry = isEdge ? { color: l.color } : { color: l.color, thickness: l.thickness };
        const found = (prices[listKey] || []).find((x) => same(x, entry));
        const sup = (prices.client_supplied || []).includes(l.id);
        const toggle = () => setPrices((o) => {
          const list = o.client_supplied || [];
          return { ...o, client_supplied: sup ? list.filter((x) => x !== l.id) : [...list, l.id] };
        });
        return (
          <div key={`${listKey}-${l.color}-${l.thickness}`} className="space-y-1">
            <PriceRow testId={`color-price-${isEdge ? "edge" : "sheet"}-${i}`}
              label={isEdge ? `${/^fita/i.test(l.color) ? "" : "Fita "}${l.color} (metro)` : `Chapa ${l.color} ${l.thickness}mm`}
              value={found ? found.price : ""} placeholder="0,00"
              onChange={(raw) => update(listKey, entry, raw)} />
            <label className="flex items-center gap-2 text-[11px] text-[#9CA3AF] cursor-pointer">
              <input type="checkbox" data-testid={`client-supplied-${isEdge ? "edge" : "sheet"}-${i}`} checked={sup} onChange={toggle} className="accent-[#E67E22]" />
              Cliente fornece este material
            </label>
          </div>
        );
      })}
      <p className="text-[11px] text-[#6B7280]">Cada cor de fita e cada chapa tem seu próprio valor. Ex: branco mais barato que chapas coloridas.</p>
    </div>
  );
};

export const brl = (v) => (+v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

export const BudgetDialog = ({ open, onOpenChange, projectId, ensureSaved, onDownload, onSend }) => {
  const [prices, setPrices] = useState(null);
  const [notes, setNotes] = useState("");
  const [budget, setBudget] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const timer = useRef(null);

  useEffect(() => {
    if (!open) return;
    setError("");
    ensureSaved().then(() => api.get("/settings/prices")).then((r) => setPrices(r.data)).catch(async (e) => setError(await errorMessage(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open || !prices) return;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      api.post(`/projects/${projectId}/budget`, { prices, notes })
        .then((r) => { setBudget(r.data); setError(""); })
        .catch(async (e) => setError(await errorMessage(e)));
    }, 350);
    return () => clearTimeout(timer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prices, open, projectId]);

  const saveDefaults = async () => {
    setBusy("save");
    try {
      await api.put("/settings/prices", prices);
      toast.success("Valores salvos como padrão");
    } catch (e) {
      toast.error(await errorMessage(e));
    } finally {
      setBusy("");
    }
  };

  const pdf = async () => {
    setBusy("pdf");
    try {
      await onDownload({ prices, notes });
    } finally {
      setBusy("");
    }
  };

  const send = async () => {
    setBusy("send");
    try {
      await onSend({ prices, notes });
    } catch (e) {
      setError(e.response?.data?.detail || "Não foi possível enviar o orçamento.");
    } finally {
      setBusy("");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="budget-dialog" className="w-[calc(100%-1rem)] max-w-4xl bg-[#181B21] border-[#2D333F] text-[#F3F4F6] max-h-[92vh] overflow-y-auto overflow-x-hidden">
        <DialogHeader><DialogTitle className="font-heading text-xl text-red-500">Orçamento</DialogTitle></DialogHeader>
        {error && <p data-testid="budget-error" className="text-sm text-red-400">{error}</p>}
        {!prices ? (
          !error && <div className="flex items-center gap-2 text-[#9CA3AF]"><Loader2 className="w-4 h-4 animate-spin" /> Carregando…</div>
        ) : (
          <div className="grid md:grid-cols-[300px_1fr] gap-6 min-w-0">
            <div className="space-y-3">
              <p className="font-heading font-semibold">Valor mão de obra (R$)</p>
              {FIELDS.map(([k, l]) => (
                <PriceRow key={k} label={l} testId={`price-${k}`} value={prices[k]}
                  onChange={(raw) => setPrices((o) => ({ ...o, [k]: raw === "" ? 0 : Number(raw) }))} />
              ))}
              <ColorPrices budget={budget} prices={prices} setPrices={setPrices} />
              <Button data-testid="budget-save-defaults" variant="outline" size="sm" onClick={saveDefaults} disabled={!!busy} className="w-full border-[#2D333F] bg-transparent">
                {busy === "save" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Salvar valores como padrão
              </Button>
            </div>
            <div className="space-y-4">
              <div className="rounded-lg border border-[#2D333F] overflow-x-auto">
                <table data-testid="budget-table" className="w-full text-sm">
                  <thead className="bg-[#13151B] text-[#9CA3AF] font-mono text-[11px] uppercase">
                    <tr>{["Item", "Qtd", "Unit.", "Total"].map((h) => <th key={h} className="text-left px-3 py-2">{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {budget?.lines.map((ln, i) => (
                      <tr key={i} data-testid={`budget-line-${ln.key}`} className="border-t border-[#2D333F]">
                        <td className="px-3 py-2">{ln.item}</td>
                        <td className="px-3 py-2 font-mono whitespace-nowrap">{String(ln.qty).replace(".", ",")} {ln.unit}</td>
                        <td className="px-3 py-2 font-mono whitespace-nowrap">{brl(ln.price)}</td>
                        <td className="px-3 py-2 font-mono font-bold whitespace-nowrap">{brl(ln.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex items-center justify-between rounded-lg bg-red-700 text-white px-4 py-3">
                <span className="font-heading font-bold">TOTAL</span>
                <span data-testid="budget-total" className="font-heading text-2xl font-extrabold">{brl(budget?.total)}</span>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-[#9CA3AF]">Observações (saem no PDF)</Label>
                <Textarea data-testid="budget-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3}
                  placeholder="Ex: prazo de entrega, forma de pagamento, validade do orçamento" className="bg-[#0F1115] border-[#2D333F]" />
              </div>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="budget-close">Fechar</Button>
          <Button data-testid="budget-send-btn" onClick={send} disabled={!prices || !!busy} variant="outline" className="border-red-500 text-red-400 bg-transparent">
            {busy === "send" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />} Enviar para o cliente
          </Button>
          <Button data-testid="budget-pdf-btn" onClick={pdf} disabled={!prices || !!busy} className="bg-red-600 hover:bg-red-700 text-white font-semibold">
            {busy === "pdf" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileText className="w-4 h-4 mr-1" />} Gerar orçamento em PDF
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
